const SCRIPT_TIMEOUT_MS = 8000;
const TOKEN_TIMEOUT_MS = 10000;
const POLL_INTERVAL_MS = 100;
const POLL_MAX_TRIES = 50;

/**
 * Carga el script de reCAPTCHA Enterprise y retorna la instancia.
 * Tiene timeout de 8s y polling para manejar scripts ya en el DOM.
 */
export const loadRecaptchaEnterprise = (siteKey) =>
  new Promise((resolve, reject) => {
    if (!siteKey) {
      reject(new Error("Falta REACT_APP_RECAPTCHA_SITE_KEY en la configuración del frontend."));
      return;
    }

    if (window.grecaptcha?.enterprise) {
      resolve(window.grecaptcha.enterprise);
      return;
    }

    let settled = false;
    let timeoutId = null;
    let pollId = null;

    const settle = (fn, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      clearTimeout(pollId);
      fn(value);
    };

    timeoutId = setTimeout(() => {
      settle(reject, new Error("Tiempo de espera agotado cargando reCAPTCHA."));
    }, SCRIPT_TIMEOUT_MS);

    const existing = document.querySelector('script[data-recaptcha-enterprise="true"]');

    if (existing) {
      let tries = 0;
      const poll = () => {
        if (window.grecaptcha?.enterprise) {
          settle(resolve, window.grecaptcha.enterprise);
        } else if (tries < POLL_MAX_TRIES) {
          tries++;
          pollId = setTimeout(poll, POLL_INTERVAL_MS);
        } else {
          settle(reject, new Error("No se pudo inicializar reCAPTCHA Enterprise."));
        }
      };
      poll();
      return;
    }

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.dataset.recaptchaEnterprise = "true";

    script.onload = () => {
      if (window.grecaptcha?.enterprise) {
        settle(resolve, window.grecaptcha.enterprise);
        return;
      }
      // Script cargó pero enterprise aún no inicializó — polling corto
      let tries = 0;
      const poll = () => {
        if (window.grecaptcha?.enterprise) {
          settle(resolve, window.grecaptcha.enterprise);
        } else if (tries < 15) {
          tries++;
          pollId = setTimeout(poll, POLL_INTERVAL_MS);
        } else {
          settle(reject, new Error("No se pudo inicializar reCAPTCHA Enterprise."));
        }
      };
      poll();
    };

    script.onerror = () => {
      settle(reject, new Error("No se pudo cargar el script de reCAPTCHA."));
    };

    document.head.appendChild(script);
  });

/**
 * Genera un token reCAPTCHA Enterprise para una acción.
 * Tiene timeout de 10s para que enterprise.ready() no cuelgue.
 */
export const getRecaptchaToken = async (siteKey, action) => {
  const enterprise = await loadRecaptchaEnterprise(siteKey);

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error("Tiempo de espera agotado generando token reCAPTCHA."));
    }, TOKEN_TIMEOUT_MS);

    enterprise.ready(async () => {
      try {
        const token = await enterprise.execute(siteKey, { action });
        clearTimeout(timeoutId);
        resolve(token);
      } catch (error) {
        clearTimeout(timeoutId);
        reject(error);
      }
    });
  });
};
